/**
 * クロスシミュダイアログ用の軽量 3D プレビュー。
 * ワールドの ReSTIR は使わず、単色シェーディングとドラッグ回転だけ持つ。
 */

import * as THREE from 'three';
import { clothBakeAabb, clothMeshToMeshData } from '../shared/cloth';
import type { ClothBake } from '../shared/types';

const COLOR = 0xc9b89a;
const FOV = 45;
const PITCH_LIMIT = 1.2;

export class ClothPreviewView {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshLambertMaterial;
  private readonly ground: THREE.Mesh;
  private readonly keyLight: THREE.DirectionalLight;
  private readonly center = new THREE.Vector3();
  private radius = 1;
  private yaw = Math.PI * 0.28;
  private pitch = 0.42;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private raf = 0;
  private disposed = false;
  private dragPointerId = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'low-power',
    });
    this.renderer.setClearColor(0x111916, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.camera = new THREE.PerspectiveCamera(FOV, 1, 0.05, 80);

    this.material = new THREE.MeshLambertMaterial({ color: COLOR, side: THREE.DoubleSide });
    this.mesh = new THREE.Mesh(new THREE.BufferGeometry(), this.material);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.scene.add(this.mesh);

    this.ground = new THREE.Mesh(
      new THREE.CircleGeometry(1, 48),
      new THREE.MeshLambertMaterial({ color: 0x151a17 })
    );
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.receiveShadow = true;
    this.ground.visible = false;
    this.scene.add(this.ground);

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.08));
    this.keyLight = new THREE.DirectionalLight(0xffffff, 1.8);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.mapSize.set(1024, 1024);
    this.keyLight.shadow.bias = -0.0004;
    this.keyLight.shadow.normalBias = 0.03;
    this.scene.add(this.keyLight);
    this.scene.add(this.keyLight.target);

    canvas.style.touchAction = 'none';
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    this.resize();
    this.loop();
  }

  setMesh(bake: ClothBake | null): void {
    this.mesh.geometry.dispose();
    if (!bake) {
      this.mesh.geometry = new THREE.BufferGeometry();
      this.ground.visible = false;
      return;
    }
    this.mesh.geometry = toPreviewGeometry(bake);
    const aabb = clothBakeAabb(bake);
    if (!aabb) {
      this.ground.visible = false;
      return;
    }
    this.center.set(
      (aabb.min.x + aabb.max.x) * 0.5,
      (aabb.min.y + aabb.max.y) * 0.5,
      (aabb.min.z + aabb.max.z) * 0.5
    );
    this.radius = Math.max(
      0.35,
      Math.hypot(aabb.max.x - aabb.min.x, aabb.max.y - aabb.min.y, aabb.max.z - aabb.min.z) * 0.5
    );
    this.ground.visible = true;
    this.ground.position.set(this.center.x, aabb.min.y, this.center.z);
    this.ground.scale.setScalar(this.radius * 3.2);
    this.syncLight();
    this.syncCamera();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    if (this.dragging && this.canvas.hasPointerCapture(this.dragPointerId)) {
      this.canvas.releasePointerCapture(this.dragPointerId);
    }
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.ground.geometry.dispose();
    if (this.ground.material instanceof THREE.Material) this.ground.material.dispose();
    this.keyLight.shadow.map?.dispose();
    this.renderer.dispose();
  }

  private loop = (): void => {
    if (this.disposed) return;
    this.resize();
    this.renderer.render(this.scene, this.camera);
    this.raf = requestAnimationFrame(this.loop);
  };

  private resize(): void {
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const nextW = Math.round(width * dpr);
    const nextH = Math.round(height * dpr);
    if (this.canvas.width === nextW && this.canvas.height === nextH) return;
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private syncLight(): void {
    const dir = new THREE.Vector3(0.92, 0.38, 0.22).normalize();
    this.keyLight.position.copy(this.center).addScaledVector(dir, this.radius * 5);
    this.keyLight.target.position.copy(this.center);
    this.keyLight.target.updateMatrixWorld();
    const cam = this.keyLight.shadow.camera;
    const extent = this.radius * 2.4;
    cam.left = -extent;
    cam.right = extent;
    cam.top = extent;
    cam.bottom = -extent;
    cam.near = this.radius * 0.4;
    cam.far = this.radius * 10;
    cam.updateProjectionMatrix();
  }

  private syncCamera(): void {
    const dist = (this.radius / Math.tan((FOV * Math.PI) / 360)) * 1.2;
    const cp = Math.cos(this.pitch);
    this.camera.position.set(
      this.center.x + Math.sin(this.yaw) * cp * dist,
      this.center.y + Math.sin(this.pitch) * dist,
      this.center.z + Math.cos(this.yaw) * cp * dist
    );
    this.camera.lookAt(this.center);
    this.camera.near = Math.max(0.02, dist * 0.02);
    this.camera.far = Math.max(20, dist * 8);
    this.camera.updateProjectionMatrix();
  }

  private onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    this.dragging = true;
    this.dragPointerId = event.pointerId;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    this.canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.dragging || event.pointerId !== this.dragPointerId) return;
    const dx = event.clientX - this.lastX;
    const dy = event.clientY - this.lastY;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    this.yaw -= dx * 0.01;
    this.pitch = Math.min(PITCH_LIMIT, Math.max(-PITCH_LIMIT, this.pitch + dy * 0.01));
    this.syncCamera();
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.dragPointerId) return;
    this.dragging = false;
    if (this.canvas.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId);
    }
  };
}

function toPreviewGeometry(bake: ClothBake): THREE.BufferGeometry {
  const data = clothMeshToMeshData(bake.mesh);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
  geometry.setIndex(new THREE.BufferAttribute(data.indices, 1));
  geometry.computeBoundingSphere();
  return geometry;
}
